import roles from "@/roles";
import axios from "@/services/axios";
import SampleService from "@/services/SampleService";
import { AxiosResponse } from "axios";
import { KeycloakInstance } from "keycloak-js";
import { computed, reactive } from "vue";

interface User {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  phoneNumber: string;
  expertFieldId?: string;
  expertFieldName?: string;
  userTitleId?: string;
  userTitleName?: string;
  researchOrganizationId?: string;
  roleName?: string;
  userRoleId?: string;
  identifier?: string;
  external?: boolean;
  enabledByAdministrator?: boolean;
  gdprConsent?: string;
  rsrid?: string;
}

interface Auth {
  token: string;
  uuid: string;
  keycloak: KeycloakInstance;
  isAuthenticated?: boolean;
  isAdminMenuLoaded: boolean;
  roles: Array<string>;
}

interface Password {
  current: string;
  new: string;
  repeat_new: string;
}

const state = reactive({
  user: {} as User,
  password: {} as Password,
  changePassword: false,
  gdprData: {} as any,
  auth: {} as Auth,
});

export default function UserRepositoryAPI() {
  async function loadUser(id: string) {
    const response = (await axios.get(
      `api/v1/User/auth/${id}`
    )) as AxiosResponse<User>;
    const user = { ...response.data } as User;
    setUser(user);
  }

  async function loadGDPRConsents() {
    const gdpr = await SampleService.getGDPR().then((data) => {
      return data;
    });
    setGDPR(gdpr);
  }

  const setUser = (data: User): void => {
    state.user = data;
  };

  const setUserDetail = (
    researcherExpertFields: any,
    researcherTitles: any,
    researcherRoles: any
  ): void => {
    const expertFieldItem = researcherExpertFields.find(
      (item: any) => item.id === state.user.expertFieldId
    );
    const userTitleItem = researcherTitles.find(
      (item: any) => item.id === state.user.userTitleId
    );
    const userRoleItem = researcherRoles.find(
      (item: any) => item.id === state.user.userRoleId
    );
    state.user.roleName = userRoleItem?.name;
    state.user.expertFieldName = expertFieldItem?.name;
    state.user.userTitleName = userTitleItem?.name;
  };

  const setGDPR = (data: any) => {
    state.gdprData = data;
  };

  async function updateUser(): Promise<AxiosResponse> {
    const payload = state.user;
    return await axios.put(`api/v1/User/${payload.id}/profile`, payload);
  }

  async function changePasswordRequest(
    userId: string,
    oldPassword: string,
    newPassword: string
  ): Promise<AxiosResponse> {
    const payload = {
      oldPassword: oldPassword,
      newPassword: newPassword,
    };
    return await axios.put(`api/v1/User/${userId}/changePassword`, payload);
  }

  const setAuth = (keycloak: KeycloakInstance): void => {
    state.auth.keycloak = keycloak;
    state.auth.token = keycloak?.token || "";
    state.auth.uuid = keycloak.tokenParsed?.sub || "";
    state.auth.isAuthenticated = keycloak.authenticated;

    if (keycloak.resourceAccess) {
      const clientId = keycloak.clientId as string;
      state.auth.roles = keycloak.resourceAccess[clientId].roles;
    }

    state.auth.isAdminMenuLoaded = [
      roles.briumSuperAdmin,
      roles.briumUniversityAdmin,
      roles.briumFacultyAdmin,
    ].some((role) => keycloak.hasResourceRole(role));
  };

  const isAdminView = (): boolean => {
    return state.auth.isAdminMenuLoaded;
  };

  const onChangePassword = (): void => {
    state.changePassword = !state.changePassword;
  };

  const hasBriumAccessRole = (): boolean => {
    return [roles.briumSuperAdmin].some((role) =>
      state.auth.roles.includes(role)
    );
  };

  const hasUniversityAccessRole = (): boolean => {
    return [roles.briumUniversityAdmin].some((role) =>
      state.auth.roles.includes(role)
    );
  };

  const hasFacultyAccessRole = (): boolean => {
    return [roles.briumFacultyAdmin].some((role) =>
      state.auth.roles.includes(role)
    );
  };

  const hasResearcherAccessRole = (): boolean => {
    return [roles.briumResearcher].some((role) =>
      state.auth.roles.includes(role)
    );
  };

  return {
    getAuth: computed(() => state.auth),
    isAdminView,
    loadUser,
    updateUser,
    getUser: computed(() => state.user),
    onChangePassword,
    changePassword: computed(() => state.changePassword),
    loadGDPRConsents,
    GDPRConsents: computed(() => state.gdprData),
    setAuth,
    setUserDetail,

    hasBriumAccessRole,
    hasUniversityAccessRole,
    hasFacultyAccessRole,
    hasResearcherAccessRole,
    changePasswordRequest,
  };
}
